/**
 * Automated Testing Sandbox Service
 * 
 * Provides an isolated, hermetic sandbox environment for automated test runners
 * (e.g. Playwright, CI/CD, and AI testing agents).
 * 
 * GUARANTEE:
 * When active, NO listings, slugs, inquiries, or test artifacts are ever written to the
 * live production Cloud Firestore database. All actions are handled in-memory and
 * scoped to sessionStorage, allowing test suites to verify complete end-to-end flows
 * (e.g. form filling, navigation, listing detail rendering) without polluting the public website.
 */

import { Property } from '../types';

export const SANDBOX_STORAGE_KEY = '__CLM_SANDBOX_STORE__';
export const SANDBOX_FLAG_KEY = 'CLM_TEST_SANDBOX';

// In-memory lookup tables for sandbox properties during the active session
const sandboxStoreById = new Map<string, Property>();
const sandboxStoreBySlug = new Map<string, Property>();

/**
 * Initializes the in-memory store from sessionStorage if running in browser
 */
function initSandboxStore(): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.sessionStorage?.getItem(SANDBOX_STORAGE_KEY);
    if (raw) {
      const list: Property[] = JSON.parse(raw);
      for (const p of list) {
        if (p.id) sandboxStoreById.set(p.id, p);
        if (p.slug) sandboxStoreBySlug.set(p.slug, p);
        if (p.publicId) sandboxStoreById.set(p.publicId, p);
      }
    }
  } catch {
    // Ignore storage parse exceptions in sandboxed test contexts
  }
}

// Initial load
initSandboxStore();

function syncToSessionStorage(): void {
  if (typeof window === 'undefined') return;
  try {
    const list = Array.from(new Set(sandboxStoreById.values()));
    window.sessionStorage?.setItem(SANDBOX_STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Ignore quota or security restrictions
  }
}

/**
 * Detects whether the current session is executing within an automated testing environment.
 */
export function isTestSandboxSession(): boolean {
  if (typeof window === 'undefined') return false;

  // Explicit window global flag
  if ((window as any).__CLM_TEST_SANDBOX__ === true) return true;

  // Session or Local Storage flags
  try {
    if (window.sessionStorage?.getItem(SANDBOX_FLAG_KEY) === 'true') return true;
    if (window.localStorage?.getItem(SANDBOX_FLAG_KEY) === 'true') return true;
  } catch {
    // Continue
  }

  // Playwright or Headless automated browser detection
  if (typeof navigator !== 'undefined' && navigator.userAgent) {
    if (/Playwright|HeadlessChrome|AutomatedTestAgent/i.test(navigator.userAgent)) {
      return true;
    }
  }

  return false;
}

/**
 * Enables test sandbox mode programmatically
 */
export function enableTestSandbox(): void {
  if (typeof window !== 'undefined') {
    (window as any).__CLM_TEST_SANDBOX__ = true;
    try {
      window.sessionStorage?.setItem(SANDBOX_FLAG_KEY, 'true');
      window.localStorage?.setItem(SANDBOX_FLAG_KEY, 'true');
    } catch {
      // Continue
    }
  }
}

/**
 * Stores a mock/sandbox property in the isolated test sandbox store.
 * Never writes to Firestore.
 */
export function saveSandboxProperty(property: any, customId?: string): { id: string; slug: string; publicId: string; property: Property } {
  initSandboxStore();

  const publicId = property.public_id || property.publicId || `CLM-SANDBOX-${Math.floor(10000 + Math.random() * 90000)}`;
  const slug = property.slug || `sandbox-ad-${publicId.toLowerCase()}`;
  const id = customId || property.id || `sandbox-prop-${publicId.toLowerCase()}`;

  const sandboxProperty: Property = {
    ...property,
    id,
    publicId,
    slug,
    title: property.title || 'Sandbox Ad',
    price: property.price || `${property.priceRaw?.toLocaleString() || '0'} XAF`,
    priceRaw: property.priceRaw || (typeof property.price === 'number' ? property.price : 0),
    location: property.location || `${property.neighborhood || 'Bastos'}, ${property.city || 'Yaoundé'}`,
    type: property.type || property.property_type || 'land',
    status: 'active',
    publishedAt: property.publishedAt || property.published_at || new Date().toISOString(),
    expiresAt: property.expiresAt || property.expires_at || new Date(Date.now() + 30 * 86400000).toISOString(),
    ownerName: property.owner_name || property.ownerName || 'QA Automation Engineer',
    ownerPhone: property.owner_phone || property.ownerPhone || '677998877',
    images: property.images && property.images.length > 0 ? property.images : [property.image || '/images/default-property.jpg'],
    image: property.image || (property.images && property.images[0]) || '/images/default-property.jpg',
    specs: property.specs || { area: property.area ? `${property.area} sqm` : '750 sqm' },
  };

  sandboxStoreById.set(id, sandboxProperty);
  sandboxStoreById.set(publicId, sandboxProperty);
  sandboxStoreBySlug.set(slug, sandboxProperty);

  syncToSessionStorage();

  return {
    id,
    slug,
    publicId,
    property: sandboxProperty,
  };
}

/**
 * Retrieves a sandbox property by its ID or public ID
 */
export function getSandboxPropertyById(id: string): Property | null {
  initSandboxStore();
  const direct = sandboxStoreById.get(id);
  if (direct) return direct;

  // Search case-insensitively
  for (const [key, val] of sandboxStoreById.entries()) {
    if (key.toLowerCase() === id.toLowerCase()) return val;
  }
  return null;
}

/**
 * Retrieves a sandbox property by its canonical slug
 */
export function getSandboxPropertyBySlug(slug: string): Property | null {
  initSandboxStore();
  const direct = sandboxStoreBySlug.get(slug);
  if (direct) return direct;

  // Search case-insensitively
  for (const [key, val] of sandboxStoreBySlug.entries()) {
    if (key.toLowerCase() === slug.toLowerCase()) return val;
  }
  return null;
}

/**
 * Checks whether an ID or slug belongs to a sandbox listing
 */
export function isSandboxListing(idOrSlug: string): boolean {
  initSandboxStore();
  return sandboxStoreById.has(idOrSlug) || sandboxStoreBySlug.has(idOrSlug);
}

/**
 * Retrieves all sandbox properties
 */
export function getAllSandboxProperties(): Property[] {
  initSandboxStore();
  return Array.from(new Set(sandboxStoreById.values()));
}

/**
 * Clears all sandbox properties
 */
export function clearSandboxStore(): void {
  sandboxStoreById.clear();
  sandboxStoreBySlug.clear();
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage?.removeItem(SANDBOX_STORAGE_KEY);
    } catch {
      // Continue
    }
  }
}
